import { useEffect, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  useGetDispatchCenters,
  type DispatchCenterListItem,
} from "@/api/dispatch-centers";
import { useCarrierScope } from "@/contexts/carrier-scope-context";
import { cn } from "@/lib/utils";

/**
 * Top-bar dispatch-center scope filter. Multi-select popover over centers
 * belonging to the carriers currently in {@link useCarrierScope}.carrierIds.
 *
 * Selection summary on the trigger:
 *  - "All centers" when empty (no narrowing — i.e. all centers under the
 *    selected carriers)
 *  - "X of Y centers" when a subset is selected
 *
 * If the active screen has opted out of center scoping via
 * {@link useCenterScopeNotApplicable}, the trigger renders disabled with an
 * explanatory tooltip.
 *
 * If no carriers are selected, the picker is hidden (there's nothing to
 * narrow).
 */
export function DispatchCenterFilter() {
  const {
    carrierIds,
    dispatchCenterIds,
    setDispatchCenterIds,
    centerScopeApplicable,
  } = useCarrierScope();
  const { data } = useGetDispatchCenters();
  const allCenters = data ?? [];

  const visibleCenters = useMemo(
    () => filterAndSortCentersForCarriers(allCenters, carrierIds),
    [allCenters, carrierIds]
  );

  // Auto-prune dispatch ids that no longer belong to any selected carrier.
  // Without this the persisted scope can hold stale ids forever after the
  // user removes a carrier from the carrier filter.
  useEffect(() => {
    if (dispatchCenterIds.length === 0) return;
    const visibleIds = new Set(visibleCenters.map((c) => c.id));
    const kept = dispatchCenterIds.filter((id) => visibleIds.has(id));
    if (kept.length !== dispatchCenterIds.length) {
      setDispatchCenterIds(kept);
    }
  }, [visibleCenters, dispatchCenterIds, setDispatchCenterIds]);

  const [open, setOpen] = useState(false);

  // Hide entirely when no carriers selected — there's nothing to filter.
  if (carrierIds.length === 0) return null;

  const total = visibleCenters.length;
  const selectedCount = dispatchCenterIds.length;
  const allSelected = total > 0 && selectedCount === total;
  const noneSelected = selectedCount === 0;

  // Empty selection = no narrowing → "All centers". (Differs from carrier
  // filter: empty there = nothing-selected because empty `carrierIds=` short-
  // circuits the server to nothing.)
  const summary = noneSelected
    ? "All centers"
    : allSelected
      ? "All centers"
      : `${selectedCount} of ${total} centers`;

  function toggle(id: string) {
    if (dispatchCenterIds.includes(id)) {
      setDispatchCenterIds(dispatchCenterIds.filter((x) => x !== id));
    } else {
      setDispatchCenterIds([...dispatchCenterIds, id]);
    }
  }

  const triggerButton = (
    <Button
      variant="outline"
      aria-label="Dispatch center filter"
      disabled={!centerScopeApplicable}
      className={cn(
        "w-[220px] h-9 px-3 justify-between text-sm",
        !noneSelected &&
          !allSelected &&
          centerScopeApplicable &&
          "bg-blue-50 border-blue-300 text-blue-900 hover:bg-blue-100"
      )}
    >
      <span className="truncate">Centers: {summary}</span>
      <ChevronDown className="h-4 w-4 opacity-50 ml-2 shrink-0" />
    </Button>
  );

  if (!centerScopeApplicable) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0} aria-label="Dispatch center (not applicable)">
              {triggerButton}
            </span>
          </TooltipTrigger>
          <TooltipContent>This screen is carrier-scoped only.</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
      <PopoverContent align="end" className="w-[280px] p-2">
        <div className="flex items-center justify-between gap-2 px-2 pb-2 border-b">
          <button
            type="button"
            className="text-xs font-medium text-blue-700 hover:underline disabled:text-gray-400 disabled:no-underline"
            disabled={allSelected || total === 0}
            onClick={() =>
              setDispatchCenterIds(visibleCenters.map((c) => c.id))
            }
          >
            Select all
          </button>
          <button
            type="button"
            className="text-xs font-medium text-gray-600 hover:underline disabled:text-gray-400 disabled:no-underline"
            disabled={noneSelected}
            onClick={() => setDispatchCenterIds([])}
          >
            Clear all
          </button>
        </div>
        <div className="max-h-[320px] overflow-y-auto py-1">
          {visibleCenters.length === 0 ? (
            <div className="px-2 py-3 text-xs text-gray-500">
              No dispatch centers for the selected carriers.
            </div>
          ) : (
            visibleCenters.map((c) => {
              const checked = dispatchCenterIds.includes(c.id);
              return (
                <label
                  key={c.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-100 cursor-pointer"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(c.id)}
                    aria-label={c.name}
                  />
                  <span className="text-sm truncate flex-1">{c.name}</span>
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">
                    {c.pickupProviderCarrier.name}
                  </span>
                </label>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Exported so tests / consumers can reuse the filter contract.
export function filterAndSortCentersForCarriers(
  centers: DispatchCenterListItem[],
  carrierIds: string[]
): DispatchCenterListItem[] {
  if (carrierIds.length === 0) return [];
  const carrierIdSet = new Set(carrierIds);
  return centers
    .filter((c) => carrierIdSet.has(c.pickupProviderCarrier.id))
    .sort((a, b) => a.name.localeCompare(b.name));
}
